// UserProfile.jsx - Using Tailwind's built-in dark mode
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserChangePassword,UserDetails } from '../../../../services/oprations/User_Bussiness_Logic';
import { 
  Mail, Phone, MapPin, Briefcase, User, Clock, Globe, 
  Smartphone, Calendar as CalendarIcon,
  Activity, Lock, UserCircle, X
} from 'lucide-react';
import ChangePasswordModal from './ChangePasswordModal';
//import ThemeToggle from '../../Theme/ThemeToggle';
import useSocket from '../../../../hooks/useSocket';

// Toast Component with dark mode support
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl animate-slide-in backdrop-blur-sm ${
      type === 'success' 
        ? 'bg-gradient-to-r from-green-500 to-green-700'
        : 'bg-gradient-to-r from-red-500 to-rose-500'
    }`}>
      <div className="text-sm font-semibold text-white">{message}</div>
      <button onClick={onClose} className="transition text-white/70 hover:text-white">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const UserProfile = () => {
  const dispatch = useDispatch();
  const socket = useSocket()
  // eslint-disable-next-line
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const menuRef = useRef(null);
  
  // Fetch user data from Redux store
  const { userDetails } = useSelector((state) => state.employeeAuth?.data || state.employeeAuth || {});
  let userData = userDetails;
  console.log("UseDetails:",userData)
  
  // Get token from localStorage
  const token = localStorage.getItem('token');
  
  // Extract nested data with fallbacks
  const {
    user_name = 'N/A',
    user_email = 'N/A',
    user_phone = 'N/A',
    user_department = 'N/A',
    user_roll = 'N/A',
    shift_start = 'N/A',
    shift_end = 'N/A',
    timezone = 'N/A',
    viewtimezone = 'N/A',
    leaveBalance = 0,
    role = 'User',
    status = 'ACTIVE',
    createdAt = null,
    createdBy = 'N/A',
    manager_Id = {},
    org_Id = {}
  } = userData || {};

  // Extract manager and organization details
  const {
    manager_name = 'N/A',
    manager_email = 'N/A',
    // eslint-disable-next-line
    department: manager_department = 'N/A'
  } = manager_Id || {};
  
  const {
    org_name = 'N/A',
    org_email = 'N/A',
    location = 'N/A'
  } = org_Id || {};

  // Format date if available
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get device enrollment info
  const devices = userData?.devices || [];
  const activeDevice = devices.find(device => device.enrollment_status === 'ACTIVE');
  // eslint-disable-next-line
  const enrollmentDate = activeDevice?.enrollment_at ? formatDate(activeDevice.enrollment_at) : 'N/A';
  // eslint-disable-next-line
  const enrollmentId = activeDevice?.enrollment_id || 'N/A';

  // Handle Change Password
  const handleChangePassword = async (passwordData) => {
    setChangePasswordLoading(true);
    try {
      const result = await dispatch(UserChangePassword(passwordData, token));
      if (result?.data?.success) {
        setToast({ show: true, message: 'Password changed successfully!', type: 'success' });
        setIsChangePasswordOpen(false);
        setIsMenuOpen(false);
      } else {
        setToast({ show: true, message: result?.data?.message || 'Failed to change password. Please try again.', type: 'error' });
      }
    } catch (err) {
      setToast({ show: true, message: 'An error occurred. Please try again.', type: 'error' });
    } finally {
      setChangePasswordLoading(false);
      setIsChangePasswordOpen(false);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!socket) return;

    socket.on("Device_Status", (data) => {
      console.log("Device Info Status:",data)
      dispatch(UserDetails(token))
    });

    return () => {
      socket.off("Device_Status");
    };
  }, [socket, dispatch, token]);

  // Stats cards data
  const stats = [
    { label: 'Leave Balance', value: `${leaveBalance} days`, icon: CalendarIcon },
    { label: 'Total Devices', value: devices.length, icon: Smartphone },
    { label: 'Department', value: user_department, icon: Briefcase },
    { label: 'Status', value: status, icon: Activity }
  ];

  return (
    <div className="min-h-screen transition-colors duration-300 bg-gray-50 dark:bg-black">
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast({ show: false, message: '', type: 'success' })} 
        />
      )}

      <div className="p-6 mx-auto space-y-6 max-w-7xl">
        {/* Profile Header */}
        <div className="relative overflow-hidden transition-all duration-300 bg-white border border-gray-200 shadow-2xl rounded-2xl dark:bg-gray-900 dark:border-gray-800">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-transparent to-blue-500/20"></div>
            <div className="absolute top-0 rounded-full -right-32 w-96 h-96 bg-blue-500/10 blur-3xl"></div>
            <div className="absolute rounded-full -bottom-32 -left-32 w-96 h-96 bg-blue-500/10 blur-3xl"></div>
          </div>
          
          <div className="relative px-8 py-8">
            <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-end">
              {/* Avatar with Glow Effect */}
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-blue-500 blur-xl opacity-60"></div>
                <div className="relative p-1 border-2 rounded-full shadow-2xl border-blue-500/50 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
                  <div className="flex items-center justify-center rounded-full w-28 h-28 bg-gradient-to-br from-blue-500/20 to-blue-500/20 dark:from-blue-500/20 dark:to-blue-500/20">
                    <UserCircle className="w-20 h-20 text-blue-500 dark:text-blue-400" />
                  </div>
                </div>
              </div>
              
              <div className="flex-1 text-center lg:text-left">
                <h1 className="text-4xl font-bold text-transparent bg-gradient-to-r from-blue-500 via-blue-500 to-blue-500 bg-clip-text">
                  {user_name}
                </h1>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-2 lg:justify-start">
                  <span className="px-3 py-1 text-sm font-medium text-blue-700 border border-blue-200 rounded-full bg-blue-100/20 dark:bg-blue-500/20 dark:border-blue-500/30 dark:text-blue-300">
                    {role}
                  </span>
                  <span className="px-3 py-1 text-sm text-gray-600 bg-gray-100 border border-gray-200 rounded-full dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300">
                    ID: {user_roll}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                {/* <ThemeToggle /> */}
                
                <button
                  onClick={() => setIsChangePasswordOpen(true)}
                  className="px-5 py-2.5 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600 text-white shadow-lg shadow-blue-500/25"
                >
                  <Lock className="w-4 h-4" />
                  <span>Change Password</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <div key={index} className="relative p-6 overflow-hidden transition-all duration-300 bg-white border border-gray-200 shadow-sm rounded-xl group dark:bg-gray-900 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-500/30 dark:shadow-none">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-gradient-to-br from-blue-500/5 to-blue-500/5 blur-2xl"></div>
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-500 p-2.5 mb-4 shadow-lg group-hover:scale-110 transition-transform">
                  <stat.icon className="w-full h-full text-white" />
                </div>
                <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-800 dark:text-white">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Details Grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Contact Information */}
          <div className="p-6 transition-all duration-300 bg-white border border-gray-200 rounded-xl dark:bg-gray-900 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-500/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-blue-100/20 dark:bg-blue-500/20">
                <Mail className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Contact Information</h3>
            </div>
            <div className="space-y-4">
              {[
                { icon: Mail, label: 'Email Address', value: user_email },
                { icon: Phone, label: 'Phone Number', value: user_phone },
                { icon: Globe, label: 'Organization', value: org_name },
                { icon: MapPin, label: 'Location', value: location }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700">
                  <item.icon className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                    <p className="font-medium text-gray-800 dark:text-gray-200">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Employment Details */}
          <div className="p-6 transition-all duration-300 bg-white border border-gray-200 rounded-xl dark:bg-gray-900 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-500/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-blue-100/20 dark:bg-blue-500/20">
                <User className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Employment Details</h3>
            </div>
            <div className="space-y-4">
              {[
                // { icon: Briefcase, label: 'Department', value: user_department },
                { icon: CalendarIcon, label: 'Join Date', value: formatDate(createdAt) },
                { icon: User, label: 'Reporting Manager', value: manager_name },
                { icon: Mail, label: 'Manager Email', value: manager_email }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700">
                  <item.icon className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                    <p className="font-medium text-gray-800 dark:text-gray-200">{item.value}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700">
                <Activity className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                  <span className={`inline-flex px-3 py-1 rounded-lg text-sm font-medium ${
                    status === 'ACTIVE'
                      ? 'bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-500/20 dark:to-teal-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'
                      : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-500/20 dark:to-rose-500/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30'
                  }`}>
                    {status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Work Schedule & Device Info */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Work Schedule */}
          <div className="p-6 transition-all duration-300 bg-white border border-gray-200 rounded-xl dark:bg-gray-900 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-500/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-blue-100/20 dark:bg-blue-500/20">
                <Clock className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Work Schedule</h3>
            </div>
            <div className="space-y-4">
              {[
                { icon: Clock, label: 'Work Shift Timing', value: `${shift_start} - ${shift_end}` },
                { icon: Globe, label: 'Work Time Zone', value: timezone },
                { icon: Globe, label: 'View Time Zone', value: viewtimezone },
                { icon: CalendarIcon, label: 'Leave Balance', value: `${leaveBalance} days` },
                { icon: User, label: 'Created By', value: createdBy }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700">
                  <item.icon className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                    <p className="font-medium text-gray-800 dark:text-gray-200">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Device Information */}
          {/* <div className="p-6 transition-all duration-300 bg-white border border-gray-200 rounded-xl dark:bg-gray-900 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-500/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-blue-100/20 dark:bg-blue-500/20">
                <Smartphone className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Device Information</h3>
            </div>
            <div className="space-y-4">
              {[
                { icon: CheckCircle, label: 'Enrollment Status', value: activeDevice ? activeDevice.enrollment_status : 'No active device', isBadge: true },
                { icon: Award, label: 'Enrollment ID', value: enrollmentId },
                { icon: CalendarIcon, label: 'Enrollment Date', value: enrollmentDate },
                { icon: Smartphone, label: 'Total Devices', value: devices.length },
                
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700">
                  <item.icon className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                    {item.isBadge ? (
                      <span className="inline-flex px-3 py-1 text-sm font-medium border rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-500/20 dark:to-teal-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30">
                        {item.value}
                      </span>
                    ) : (
                      <p className="font-medium text-gray-800 dark:text-gray-200">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div> */}

          {/* Organization Info */}
          <div className="p-6 transition-all duration-300 bg-white border border-gray-200 rounded-xl dark:bg-gray-900 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-500/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-blue-100/20 dark:bg-blue-500/20">
                <Globe className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Organization Information</h3>
            </div>
            <div className="space-y-4">
              {[
                { label: 'Organization Name', value: org_name },
                { label: 'Organization Email', value: org_email },
                // { label: 'Total Users', value: org_Id.total_users || 'N/A' },
                // { label: 'Total Devices', value: org_Id.total_devices || 'N/A' }
              ].map((item, idx) => (
                <div key={idx} className="p-4 border border-gray-100 rounded-lg bg-gray-50 dark:bg-gray-800/50 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                  <p className="font-medium text-gray-800 dark:text-gray-200">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
        onSubmit={handleChangePassword}
        loading={changePasswordLoading}
      />
    </div>
  );
};

export default UserProfile;
